import React, {ReactElement, useCallback, useEffect, useMemo} from "react"
import classNames from "classnames"
import {AsyncResultComponent} from "../../containers/AsyncResult/AsyncResult"
import {usePaging} from "../../hooks/usePaging"
import {
	CheckDeposit,
	CheckDepositStatus,
	CheckDepositVendors,
	findCheckDepositRequest,
} from "../../resources/checkDeposits"
import {useAccessToken, useUserType} from "../../services/auth"
import {useQueryState} from "use-location-state"
import SearchBox from "../SearchBox/SearchBox"
import DatePickerWithPresets, {DatePickerPresetKeys} from "../DatePicker/DatePicker"
import {Filter} from "../Filter/Filter"
import {ErrorDocument, Meta} from "../../resources/common"
import {startCase, upperFirst} from "lodash"
import {useRefreshToken} from "../../hooks/useRefreshToken"
import {DataTableActionHeader, DataTableCard, TablePending} from "../DataTable/DataTable"
import {downloadFile} from "../../utilities/file"
import {useToasts} from "react-toast-notifications"
import {OrgFilter} from "../OrgFilter/OrgFilter"
import {findOrgs} from "../../resources/org"
import {Org} from "../../resources/org"
import {AsyncResult} from "../../types/asyncResult"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {AllowedCheckDepositColumns} from "./CheckDeposit.types"
import CheckDepositsTable from "./CheckDepositTable"
import {Cents} from "../../resources/accountHold"
import {CurrencyRangeFilter} from "../Filter/CurrencyRangeFilter"

interface CheckDepositsProps {
	token?: string
	limit?: number
	includedColumns: Array<AllowedCheckDepositColumns>
	enableTitle?: boolean
	enableOrgFilter?: boolean
	fullHeight?: boolean
	showTabsOnHeader?: boolean
	disableDrawer?: boolean
	enableExport?: boolean
	enableStatusFilter?: boolean
	title?: string
	stickyAction?: boolean
	enablePaging?: boolean
	enableSearch?: boolean
	enableDateFilter?: boolean
	enableRowClick?: boolean
	customerId?: string
	accountId?: string
	initialStatusFilter?: Array<CheckDepositStatus> | []
}

export function CheckDeposits({
	token,
	limit = 20,
	includedColumns,
	enableTitle = false,
	fullHeight = false,
	showTabsOnHeader = false,
	disableDrawer = false,
	enableExport = true,
	stickyAction = false,
	enablePaging = true,
	enableOrgFilter = true,
	enableStatusFilter = true,
	enableSearch = true,
	enableDateFilter = true,
	enableRowClick = true,
	title,
	customerId,
	accountId,
	initialStatusFilter = [],
}: CheckDepositsProps): ReactElement {
	const accessToken = token ?? useAccessToken()
	const [refreshToken, refresh] = useRefreshToken()
	const userType = useUserType()
	const {addToast} = useToasts()
	const [query, setQuery] = useQueryState("query", "")
	const [searchBox, setIsSearchLoading] = SearchBox(query, "Search for check", 500, onSearch)
	const [since, setSince] = useQueryState("filter[since]", "")
	const [until, setUntil] = useQueryState("filter[until]", "")
	const [filterByVendor, setFilterByVendor] = useQueryState<string[]>("filter[vendor]", [
		CheckDepositVendors.Ensenta,
		CheckDepositVendors.Veryfi,
		CheckDepositVendors.Smartpay,
	])
	const [filteredByStatus, setFilteredByStatus] = useQueryState<string[]>("filter[status]", initialStatusFilter)
	const [filteredByOrg, setFilteredByOrg] = useQueryState<string[]>("filter[orgs]", [])
	const [selectedCheckDepositId, setSelectedCheckDepositId] = useQueryState("checkDepositId", "")
	const [sortBy, setSortBy] = useQueryState("sort", "-createdAt")
	const [fromAmount, setFromAmount] = useQueryState<Cents | "">("filter[fromAmount]", "")
	const [toAmount, setToAmount] = useQueryState<Cents | "">("filter[toAmount]", "")

	const escFunction = useCallback((event: KeyboardEvent) => {
		if (event.key === "Escape") {
			setSelectedCheckDepositId("")
		}
		return null
	}, [])

	useEffect(() => {
		document.addEventListener("keydown", escFunction, false)
	}, [])

	const statusFilterMap = new Map(
		Object.entries(CheckDepositStatus).map((status) => [upperFirst(status[0]), startCase(status[1])])
	)

	const vendorFilterMap = new Map(
		Object.entries(CheckDepositVendors).map((vendor) => [upperFirst(vendor[0]), startCase(vendor[1])])
	)

	const dateTime = (
		<DatePickerWithPresets
			calendarPosition={"right"}
			presets={[
				DatePickerPresetKeys.allTime,
				DatePickerPresetKeys.yesterday,
				DatePickerPresetKeys.lastMonth,
				DatePickerPresetKeys.last3Months,
				DatePickerPresetKeys.last6Months,
				DatePickerPresetKeys.last30Days,
				DatePickerPresetKeys.last7Days,
				DatePickerPresetKeys.custom,
			]}
			onDateChanged={(s, u) => {
				setSince(s)
				setUntil(u)
			}}
		/>
	)

	const statusFilter = (
		<Filter
			onFilterFunc={() => reset(limit)}
			options={statusFilterMap}
			setStatuses={setFilteredByStatus}
			statuses={filteredByStatus}
			title="Status"
		/>
	)

	const vendorFilter = (
		<Filter
			onFilterFunc={() => reset(limit)}
			options={vendorFilterMap}
			setStatuses={setFilterByVendor}
			statuses={filterByVendor}
			title="Vendor"
		/>
	)

	const amountFilter = (
		<CurrencyRangeFilter
			placeholder={"Amount"}
			presets={[]}
			from={fromAmount}
			setFrom={setFromAmount}
			to={toAmount}
			setTo={setToAmount}
		/>
	)

	const getCheckDeposit = (offset: number, limit: number) => {
		return findCheckDepositRequest({
			accessToken,
			offset,
			limit,
			since,
			until,
			statuses: filteredByStatus,
			vendors: filterByVendor,
			orgs: filteredByOrg,
			include: "customer,org",
			requestType: "json",
			searchQuery: query,
			sort: sortBy,
			customerId,
			accountId,
			fromAmount,
			toAmount,
		})
	}

	function onSearch(searchTerm: string) {
		setSelectedCheckDepositId("")
		setQuery(searchTerm)
		reset(limit)
	}

	const [result, hasPrev, hasNext, prev, next, hasResults, reset] = usePaging(
		limit,
		getCheckDeposit,
		(x) => x.data.length,
		[
			refreshToken,
			query,
			since,
			until,
			sortBy,
			[...filteredByStatus, ...filteredByOrg, ...filterByVendor].join(","),
			fromAmount,
			toAmount,
		],
		"",
		true
	)
	const orgs =
		userType === "unit" || userType === "bank"
			? useAsyncResult(() => findOrgs(accessToken, 0, 10000), [])
			: AsyncResult.pending<Org[], ErrorDocument>()

	const orgFilter = useMemo(
		() => OrgFilter(orgs, setFilteredByOrg, filteredByOrg, reset, limit),
		[orgs, setFilteredByOrg, filteredByOrg.join(","), reset, limit]
	)

	useEffect(() => {
		let isActive = true
		if (isActive) {
			result.match(
				() => null,
				() => setIsSearchLoading(false),
				() => setIsSearchLoading(false)
			)
		}
		return () => {
			isActive = false
		}
	}, [result])

	function toggleSort() {
		setSortBy(sortBy == "-createdAt" ? "createdAt" : "-createdAt")
		reset(limit)
	}

	async function exportCheckDeposits() {
		const response = await findCheckDepositRequest({
			accessToken,
			offset: 0,
			since,
			until,
			statuses: filteredByStatus,
			orgs: filteredByOrg,
			requestType: "csv",
			searchQuery: query,
			include: "org",
			sort: sortBy,
			accountId,
		})

		response.match(
			(cd) => downloadFile("Check Deposit.csv", cd, "text/csv"),
			() => {
				addToast("Something went wrong", {appearance: "warning"})
			}
		)
		return true
	}

	return (
		<DataTableCard className={"check-deposit-card"}>
			<DataTableActionHeader
				enableSticky={fullHeight}
				searchBox={enableSearch ? searchBox : null}
				dateTimePicker={enableDateFilter ? dateTime : null}
				filters={[
					...(enableOrgFilter ? [orgFilter] : []),
					...(enableStatusFilter ? [statusFilter] : []),
					vendorFilter,
					amountFilter,
				]}
				title={enableTitle ? (title ? title : "Check Deposits") : null}
				exportFunc={enableExport ? exportCheckDeposits : undefined}
				className={classNames(showTabsOnHeader && "with-title-bar-tabs")}
			/>
			<AsyncResultComponent
				asyncResult={result}
				pendingComponent={<TablePending numberOfRows={includedColumns.length} />}
			>
				{({value}) => {
					const checkDeposits = value as {data: CheckDeposit[]; meta: Meta; included: any[]}
					return (
						<CheckDepositsTable
							checkDeposits={checkDeposits.data}
							selectedCheckDepositId={selectedCheckDepositId}
							setSelectedCheckDepositId={setSelectedCheckDepositId}
							meta={checkDeposits.meta}
							hasResults={hasResults}
							hasPrev={hasPrev}
							hasNext={hasNext}
							prev={prev}
							next={next}
							refresh={refresh}
							isUsingPaging={enablePaging}
							include={checkDeposits.included}
							token={accessToken}
							fullHeight={fullHeight}
							includedColumns={includedColumns}
							disableDrawer={disableDrawer}
							sortFunction={toggleSort}
							sortBy={sortBy}
							stickyAction={stickyAction}
							showTabsOnHeader={showTabsOnHeader}
							enableRowClick={enableRowClick}
						/>
					)
				}}
			</AsyncResultComponent>
		</DataTableCard>
	)
}
